import { TypeManagerDecorator } from "../../../main/type.map.service";
import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ElementRef,
    Input,
    Output,
    EventEmitter,
    OnInit,
    ViewChild,
    ViewEncapsulation,
    inject,
    HostBinding,
} from "@angular/core";
import { S25Util } from "../../../util/s25-util";
import { IngestionFieldDefinition, IngestionPoint, DataIngestionService, AnswerType } from "../data.ingestion.service";
import {
    INGESTION_POINT_TYPE_ID,
    STARS_NUMERIC_SCALE_MAX,
    STARS_NUMERIC_SCALE_MIN,
    INGESTION_AUTHLESS_RESTRICTED_FIELD_TYPES,
    isAuthlessRestrictedFieldType,
} from "../data.ingestion.const";
import { INGESTION_SECURITY_SERVICE } from "../ingestion.security.service";
import {
    parseObjectOccurrenceFieldConfig,
    type ObjectOccurrenceFieldConfig,
} from "../field-types/object.occurrence.field.config";
import { parseCategoriesFieldConfig, stringifyCategoriesFieldConfig } from "../field-types/categories.field.config";
import {
    parseSectionFieldConfig,
    stringifySectionFieldConfig,
    type SectionFieldConfig,
} from "../field-types/section.field.config";
import { parseFormStyleConfig, stringifyFormStyleConfig, type FormStyleConfig } from "../field-types/form.style.config";
import {
    DEFAULT_FORM_TITLE_COLOR,
    DEFAULT_SECTION_HEADING_COLOR,
    FORM_FONT_SIZE_OPTIONS,
    type FormFontSize,
} from "../field-types/ingestion.form.font-size";
import { S25FilestoreImageComponent } from "../../../standalone/s25.filestore.image.component";
import {
    getElectionIdsFromFieldConfig,
    parseElectionPositionFieldConfig,
    serializeElectionPositionFieldConfig,
} from "../field-types/election.position.field.config";
import { parseLive25EventFormFieldConfig } from "../field-types/live.25.event.form.field.config";
import { EventCreationFormService, EventFormConfigData } from "../../../services/event.creation.form.service";
import { ElectionApi } from "../../student-engagement/elections/election.api";
import { SearchCriteriaDataUtil } from "../../s25-multiselect/search.criteria.data.util";
import type { S25ItemI } from "../../../pojo/S25ItemI";
import type { SearchCriteriaType } from "../../../pojo/SearchCriteriaI";
import { Rules } from "../../s25-rule-tree/s25.rule.const";
import { WorkflowService } from "../../workflow/service/workflow.service";
import { Workflow } from "../../workflow/service/Workflow";
import { computed, signal } from "@angular/core";
import { s25Resource } from "../../../util/s25.resource";
import { NameService } from "../../../services/name.service";
import { Item } from "../../../pojo/Item";
import { DropDownItem } from "../../../pojo/DropDownItem";
import { Nullable } from "../../../pojo/Util";
import { SearchCriteriaService } from "../../../services/search/search-criteria/search.criteria.service";
import { AlertService } from "../../../services/alert.service";
import { ModalService } from "../../modal/modal.service";
import { IngestionAdminAccessRulesComponent } from "./ingestion.admin.access.rules.component";
import { IngestionAdminFormFieldsTabComponent } from "./ingestion.admin.form.fields.tab.component";
import { getPaletteTileLabel } from "./ingestion.field.palette.const";
import { WorkflowAddFormActionsModalData } from "../../workflow/modal/workflow.add.form.actions.modal.data";
import { RuleTreeService, type Rule } from "../../../services/rule.tree.service";
import { ItemService } from "../../../util/items/item.service";
import { buildWorkflowDropdownItems, findSelectedWorkflowDropdownItem } from "./ingestion.admin.workflow.dropdown.util";
import { loadIngestionShowHideRules } from "./ingestion.admin.preview.util";
import { invalidateIngestionFormRulesFieldCache } from "./ingestion.admin.form.rules.util";
import {
    stampCustAtrbFromSearchItem,
    stampMissingCustAtrbFromSearchItems,
    type CustAtrbSearchItem,
} from "../ingestion.cust.atrb.stamp.util";

type SuggestedField = { label: string; fieldType: IngestionFieldDefinition["fieldType"] };

type FormBuilderTabId = "settings" | "workflow" | "access" | "fields" | "rules" | "preview";

@TypeManagerDecorator("s25-ng-admin-ingestion-form")
@Component({
    selector: "s25-ng-admin-ingestion-form",
    template: `
        @if (init) {
            <div class="form-content">
                <div class="form-builder-tabs" role="tablist" aria-label="Form builder">
                    @for (tab of getFormBuilderTabs(); track tab.id) {
                        <button
                            type="button"
                            role="tab"
                            class="form-builder-tab"
                            [class.active]="activeTab() === tab.id"
                            [attr.aria-selected]="activeTab() === tab.id"
                            (click)="setActiveTab(tab.id)"
                        >
                            {{ tab.label }}
                        </button>
                    }
                </div>

                <div class="form-builder-tab-panel" [hidden]="activeTab() !== 'settings'">
                    <div class="collapsible-section-content">
                        <div class="c-margin-bottom--single">
                            <label class="ngBlock">
                                Form Name:
                                <input
                                    class="c-input"
                                    type="text"
                                    [(ngModel)]="ingestionPoint.ingestionPointName"
                                    placeholder="Enter form name"
                                />
                            </label>
                        </div>

                        <div class="c-margin-bottom--single">
                            <label class="ngBlock">
                                Description (optional):
                                <textarea
                                    class="cn-form__control"
                                    [(ngModel)]="ingestionPoint.description"
                                    placeholder="Enter a description for this form"
                                    rows="3"
                                ></textarea>
                            </label>
                        </div>

                        @let formStyleConfig = getFormStyleConfig();
                        <div class="c-margin-bottom--single form-appearance-section">
                            <h4 class="c-margin-bottom--half">Form appearance</h4>
                            <p class="form-help-text c-margin-bottom--single">
                                Customize the respondent-facing form title, header image, and post-submit message.
                            </p>
                            <div class="c-margin-bottom--single">
                                <label class="ngBlock c-margin-bottom--half">Success message</label>
                                <s25-ng-rich-text-editor
                                    [(modelValue)]="ingestionPoint.successMessage"
                                ></s25-ng-rich-text-editor>
                                <small class="form-text"
                                    >Shown after a successful submission. Defaults to "Thank you for your submission."
                                    when blank.</small
                                >
                            </div>
                            <div class="c-margin-bottom--single">
                                <label class="ngBlock">Header image</label>
                                <s25-ng-filestore-image
                                    #formHeaderImage
                                    [value]="formStyleConfig.headerImageKey ?? ''"
                                    (valueChange)="setFormStyleHeaderImageKey($event)"
                                    [objectType]="-1"
                                    [objectId]="-1"
                                    [fileKey]="formHeaderImageFileKey()"
                                    [saveButton]="false"
                                />
                                <small class="form-text"
                                    >Choose an image and alt text, then click Save form to upload. Preview shows the
                                    header after save.</small
                                >
                            </div>
                            <div class="c-margin-bottom--single">
                                <label for="form-title-font-size">Title font size</label>
                                <select
                                    id="form-title-font-size"
                                    class="c-input"
                                    [ngModel]="formStyleConfig.titleFontSize"
                                    (ngModelChange)="setFormStyleTitleFontSize($event)"
                                >
                                    @for (sizeOption of FORM_FONT_SIZE_OPTIONS; track sizeOption.value) {
                                        <option [ngValue]="sizeOption.value">{{ sizeOption.label }}</option>
                                    }
                                </select>
                            </div>
                            <div class="c-margin-bottom--single form-title-color-row">
                                <label for="form-title-color">Title color</label>
                                <div class="form-title-color-controls">
                                    <input
                                        id="form-title-color"
                                        type="color"
                                        class="form-title-color-swatch"
                                        [ngModel]="
                                            S25Util.hexForColorPicker(
                                                formStyleConfig.titleColor,
                                                DEFAULT_FORM_TITLE_COLOR
                                            )
                                        "
                                        (ngModelChange)="setFormStyleTitleColor($event)"
                                        title="Pick title color"
                                        aria-label="Pick form title color"
                                    />
                                    <input
                                        class="c-input form-title-color-text"
                                        type="text"
                                        [ngModel]="formStyleConfig.titleColor ?? ''"
                                        (ngModelChange)="setFormStyleTitleColor($event)"
                                        placeholder="{{ DEFAULT_FORM_TITLE_COLOR }}"
                                    />
                                </div>
                            </div>
                        </div>

                        <div class="c-margin-bottom--single">
                            <label class="ngBlock">
                                Category:
                                <div class="category-display">{{ ingestionPoint.category }}</div>
                            </label>
                        </div>

                        <div class="c-margin-bottom--single">
                            <label class="ngBlock">
                                Sub-Category:
                                <s25-ng-ingestion-subcategory-selector
                                    [category]="ingestionPoint.category!"
                                    [subCategory]="ingestionPoint.subCat ?? ''"
                                    [isEditing]="!ingestionPoint.enabled"
                                    [allowClear]="true"
                                    [onSubCategoryChange]="onSubCategoryChange"
                                ></s25-ng-ingestion-subcategory-selector>
                            </label>
                        </div>

                        <div class="c-margin-bottom--single">
                            <div class="ngBlock" role="group" aria-labelledby="ingestion-form-use-label">
                                <span id="ingestion-form-use-label">Can be used for:</span>
                                <s25-ng-ingestion-form-use-help />
                                <div class="use-checkbox-group">
                                    <s25-ng-checkbox [(value)]="ingestionPoint.use!.registration">
                                        Registration
                                    </s25-ng-checkbox>
                                    <s25-ng-checkbox [(value)]="ingestionPoint.use!.eventSurvey">
                                        Event Survey
                                    </s25-ng-checkbox>
                                    <s25-ng-checkbox [(value)]="ingestionPoint.use!.eventCreation">
                                        Event Creation
                                    </s25-ng-checkbox>
                                    <s25-ng-checkbox [(value)]="ingestionPoint.use!.orgCreation">
                                        Org Creation
                                    </s25-ng-checkbox>
                                    <s25-ng-checkbox [(value)]="ingestionPoint.use!.orgEdit">
                                        Org Edit
                                    </s25-ng-checkbox>
                                    <s25-ng-checkbox [(value)]="ingestionPoint.use!.orgJoin">
                                        Join Organization
                                    </s25-ng-checkbox>
                                    <s25-ng-checkbox [(value)]="ingestionPoint.use!.eventEdit">
                                        Event Edit
                                    </s25-ng-checkbox>
                                    <s25-ng-checkbox [(value)]="ingestionPoint.use!.electionCandidate">
                                        Election candidate
                                    </s25-ng-checkbox>
                                </div>
                            </div>
                        </div>

                        <div class="c-margin-bottom--single">
                            <s25-ng-checkbox [(value)]="ingestionPoint.enabled!"> Form Enabled </s25-ng-checkbox>
                        </div>

                        @if (
                            !ingestionPoint.use?.eventCreation &&
                            !ingestionPoint.use?.eventEdit &&
                            !ingestionPoint.use?.orgJoin
                        ) {
                            <div class="c-margin-bottom--single">
                                <s25-ng-checkbox
                                    [(value)]="ingestionPoint.authless!"
                                    (valueChange)="onAuthlessChange($event)"
                                >
                                    Allow authless submissions
                                </s25-ng-checkbox>
                            </div>
                        }

                        <div class="c-margin-bottom--single">
                            <s25-ng-checkbox [(value)]="ingestionPoint.allowEditAfterSubmit!">
                                Allow editing after submission
                            </s25-ng-checkbox>
                        </div>

                        <div class="c-margin-bottom--single">
                            <h4 class="c-margin-bottom--half">Active Date Range</h4>
                            <p class="form-help-text c-margin-bottom--single">
                                Set when this form is available for submissions. Leave blank to allow submissions at any
                                time.
                            </p>

                            <div class="date-range-section">
                                <div class="c-margin-bottom--single">
                                    <label class="ngBlock">
                                        Active Start Date (optional):
                                        <input
                                            class="c-input"
                                            type="datetime-local"
                                            [(ngModel)]="ingestionPoint.activeStartDate"
                                            [placeholder]="'Leave blank for no start date'"
                                        />
                                    </label>
                                </div>

                                <div class="c-margin-bottom--single">
                                    <label class="ngBlock">
                                        Active End Date (optional):
                                        <input
                                            class="c-input"
                                            type="datetime-local"
                                            [(ngModel)]="ingestionPoint.activeEndDate"
                                            [placeholder]="'Leave blank for no end date'"
                                        />
                                    </label>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div class="form-builder-tab-panel" [hidden]="activeTab() !== 'workflow'">
                    <div class="collapsible-section-content">
                        <div class="c-margin-bottom--single">
                            <div class="ngBlock">
                                Workflow Assignment:
                                <div class="workflow-assignment-section">
                                    <div class="workflow-option">
                                        <label class="workflow-option-label">
                                            <input
                                                type="radio"
                                                name="workflowType"
                                                value="none"
                                                [(ngModel)]="workflowAssignmentType"
                                                (ngModelChange)="onWorkflowTypeChange()"
                                            />
                                            <span class="workflow-option-text">No Workflow Process</span>
                                        </label>
                                    </div>

                                    <div class="workflow-option">
                                        <label class="workflow-option-label">
                                            <input
                                                type="radio"
                                                name="workflowType"
                                                value="direct"
                                                [(ngModel)]="workflowAssignmentType"
                                                (ngModelChange)="onWorkflowTypeChange()"
                                            />
                                            <span class="workflow-option-text">Direct Workflow Assignment</span>
                                        </label>
                                        @if (workflowAssignmentType === "direct") {
                                            <div class="workflow-selector">
                                                <s25-generic-dropdown
                                                    [items]="directWorkflowDropdownItems()"
                                                    [searchEnabled]="true"
                                                    [placeholder]="'Select workflow process'"
                                                    [value]="selectedDirectWorkflowItem()"
                                                    (valueChange)="onDirectWorkflowDropdownChange($event)"
                                                    [ariaLabel]="'Direct workflow assignment'"
                                                />
                                                @if (isAssignedWorkflowDisabled()) {
                                                    <div class="c-alert c-alert--warning c-margin-top--quarter">
                                                        The assigned workflow (ID {{ ingestionPoint.workflowId }}) is
                                                        currently disabled and does not appear in the list. Enable it in
                                                        the Workflow admin to see it here.
                                                    </div>
                                                }
                                                @if (missingFormContextAction()) {
                                                    <div class="cn-alert cn-alert--warning c-margin-top--quarter">
                                                        The selected workflow will not run for this form's submissions
                                                        as it has no action referencing this form as its form context.
                                                        <br />
                                                        To fix this, open the workflow and set this form as the form
                                                        context on the intended action.
                                                    </div>
                                                }
                                            </div>
                                        }
                                    </div>

                                    <div class="workflow-option">
                                        <label class="workflow-option-label">
                                            <input
                                                type="radio"
                                                name="workflowType"
                                                value="default"
                                                [(ngModel)]="workflowAssignmentType"
                                                (ngModelChange)="onWorkflowTypeChange()"
                                            />
                                            <span class="workflow-option-text"
                                                >Default Workflow for Rules-Based Assignment</span
                                            >
                                        </label>
                                        @if (workflowAssignmentType === "default") {
                                            <div class="workflow-selector">
                                                <s25-generic-dropdown
                                                    [items]="defaultWorkflowDropdownItems()"
                                                    [searchEnabled]="true"
                                                    [placeholder]="'Select default workflow process'"
                                                    [value]="selectedDefaultWorkflowItem()"
                                                    (valueChange)="onDefaultWorkflowDropdownChange($event)"
                                                    [ariaLabel]="'Default workflow assignment'"
                                                />
                                                @if (missingDefaultFormContextAction()) {
                                                    <div class="cn-alert cn-alert--warning c-margin-top--quarter">
                                                        If no rules match, the default workflow will not run for this
                                                        form's submissions as it has no action referencing this form as
                                                        its form context.
                                                        <br />
                                                        To fix this, open the workflow and set this form as the form
                                                        context on the intended action.
                                                    </div>
                                                }
                                            </div>
                                        }
                                    </div>
                                </div>
                            </div>
                            @if (
                                workflowAssignmentType === "none" &&
                                (ingestionPoint.use?.registration ||
                                    ingestionPoint.use?.eventSurvey ||
                                    ingestionPoint.use?.eventCreation ||
                                    ingestionPoint.use?.eventEdit ||
                                    ingestionPoint.use?.orgCreation ||
                                    ingestionPoint.use?.orgEdit ||
                                    ingestionPoint.use?.orgJoin)
                            ) {
                                <div class="cn-alert cn-alert--warning c-margin-top--quarter">
                                    This form has a form-use configured but no workflow assigned. The form-use will not
                                    trigger any workflow actions.
                                </div>
                            }
                            <div class="form-help-text">
                                <strong>Direct Assignment:</strong> All form submissions will be assigned to the
                                selected workflow process.<br />
                                <strong>Default for Rules:</strong> Use rules to determine workflow assignment, with the
                                selected workflow as fallback when no rules match.
                            </div>

                            @if (referencingWorkflows.length) {
                                <div class="referencing-workflows">
                                    <strong>Referenced by Workflows:&nbsp;</strong>
                                    <div class="referencing-list">
                                        @for (wf of referencingWorkflows; track wf.workflowProcessId) {
                                            <span class="referencing-name">{{ wf.workflowProcessName }}</span>
                                        }
                                    </div>
                                </div>
                            }
                        </div>
                    </div>
                </div>

                <div class="form-builder-tab-panel" [hidden]="activeTab() !== 'access'">
                    <!-- Form access (security) -->
                    <div class="form-access-section c-margin-bottom--double">
                        <div class="collapsible-section-content">
                            <p class="form-access-description">
                                Control who can fill out the form, edit the form details, copy it, and perform other
                                actions
                            </p>
                            <s25-ng-ingestion-admin-access-rules
                                [ingestionPointId]="ingestionPointId"
                            ></s25-ng-ingestion-admin-access-rules>
                        </div>
                    </div>
                </div>

                <s25-ng-ingestion-admin-form-fields-tab
                    #fieldsTab
                    [editor]="this"
                    [hidden]="activeTab() !== 'fields'"
                />

                @if (activeTab() === "rules" && canCreateForm()) {
                    <div class="form-builder-tab-panel">
                        <s25-ng-ingestion-admin-rules [ingestionPointId]="ingestionPointId" />
                    </div>
                }

                <div class="form-builder-tab-panel" [hidden]="activeTab() !== 'preview'">
                    <p class="form-help-text preview-tab-banner">
                        Interactive preview — answers are not saved. Shows unsaved field changes until you save the
                        form.
                    </p>
                    @if (activeTab() === "preview") {
                        <ng-container *s25-ng-trigger-rerender="previewRemountKey()">
                            <s25-ng-ingestion-form
                                [previewMode]="true"
                                [previewIngestionPoint]="ingestionPoint"
                                [previewFieldDefinitions]="previewFieldDefinitionsClone()"
                                [previewShowHideRules]="previewShowHideRules()"
                                [showHiddenFieldPlaceholders]="true"
                            />
                        </ng-container>
                    }
                </div>

                <div class="form-actions">
                    <s25-ng-button [type]="'primary'" [onClick]="saveAll">Save form</s25-ng-button>
                    @if (ingestionPoint?.ingestionPointId) {
                        <s25-ng-button
                            [type]="'outline'"
                            [onClick]="openDownloadQrModal"
                            [buttonClass]="'c-margin-left--single'"
                        >
                            Download QR Code
                        </s25-ng-button>
                    }
                    @if (ingestionPoint?.deleted) {
                        <s25-ng-button
                            [type]="'success'"
                            [onClick]="undeleteForm"
                            [buttonClass]="'c-margin-left--single'"
                        >
                            Restore Form
                        </s25-ng-button>
                    }
                    <button class="c-margin-left--single aw-button aw-button--danger--outline" (click)="cancel()">
                        Cancel
                    </button>
                </div>
            </div>
        }
    `,
    styles: `
        :host {
            display: block;
            padding-bottom: 80px;
        }

        .form-content {
            flex: 1;
            min-width: 0;
        }

        .form-builder-tabs {
            display: flex;
            flex-wrap: wrap;
            gap: 0;
            border-bottom: 1px solid var(--color--border);
            margin-bottom: 1.5rem;
        }

        .form-builder-tab {
            padding: 12px 20px;
            border: none;
            background: none;
            cursor: pointer;
            border-bottom: 2px solid transparent;
            color: var(--color--text-muted);
            font-size: 1rem;
            font-weight: 500;
            margin-bottom: -1px;
            transition:
                color var(--transition-short),
                border-color var(--transition-short);
        }

        .form-builder-tab:hover {
            color: var(--color--text);
        }

        .form-builder-tab.active {
            color: var(--color--link-text);
            border-bottom-color: var(--color--link-text);
            font-weight: 600;
        }

        .form-builder-tab-panel {
            min-width: 0;
        }

        .preview-tab-banner {
            margin-bottom: 1rem;
        }

        .form-access-section {
            border: 1px solid var(--color--border);
            border-radius: 8px;
            overflow: hidden;
        }

        .form-access-description {
            color: var(--color--text-muted);
            font-style: italic;
            margin-bottom: 1rem;
            font-size: 0.9rem;
        }

        .collapsible-section-content {
            padding: 20px;
            background: var(--color--surface-bg);
        }

        .category-display {
            padding: 0.5rem;
            background-color: var(--color--page-bg);
            border: 1px solid var(--color--border);
            border-radius: 4px;
            min-height: 38px;
            display: flex;
            align-items: center;
            font-weight: 500;
            color: var(--color--text-muted);
        }

        .use-checkbox-group {
            display: inline-flex;
            gap: 1rem;
            align-items: center;
        }

        .form-actions {
            position: fixed;
            bottom: 1em;
            right: 4%;
            z-index: 1000;
            background: var(--color--page-bg);
            border: 1px solid var(--color--border);
            border-radius: 8px;
            padding: 12px 20px;
            box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
            display: flex;
            gap: 8px;
            align-items: center;
            transition:
                right 0.2s ease,
                left 0.2s ease;
        }

        :host.fields-palette-gutter-open .form-actions {
            right: max(1rem, calc(280px + 4%));
            z-index: 1002;
        }

        @media (max-width: 768px) {
            .form-actions {
                left: 1rem;
                right: 1rem;
                flex-wrap: wrap;
                justify-content: center;
            }

            :host.fields-palette-gutter-open .form-actions {
                left: 1rem;
                right: 1rem;
                display: none;
            }
        }

        .rule-description {
            color: #666;
            font-style: italic;
            margin-bottom: 1rem;
            font-size: 0.9rem;
        }

        .workflow-assignment-section {
            border: 1px solid var(--color--border);
            border-radius: 6px;
            padding: 16px;
            background-color: var(--color--page-bg);
            margin-top: 8px;
        }

        .workflow-option {
            margin-bottom: 12px;
        }

        .workflow-option:last-child {
            margin-bottom: 0;
        }

        .workflow-option-label {
            display: flex;
            align-items: center;
            cursor: pointer;
            font-weight: 500;
            color: var(--color--text-muted);
        }

        .workflow-option-label input[type="radio"] {
            margin-right: 8px;
        }

        .workflow-option-text {
            font-size: 14px;
        }

        .workflow-selector {
            margin-top: 8px;
            margin-left: 24px;
        }

        .workflow-selector s25-generic-dropdown {
            display: block;
            width: 100%;
            max-width: 400px;
        }

        .cn-alert {
            margin-left: 0;
        }

        .date-range-section {
            border: 1px solid var(--color--border);
            border-radius: 6px;
            padding: 16px;
            background-color: var(--color--page-bg);
            margin-top: 8px;
        }

        .referencing-workflows {
            margin-top: 12px;
            padding: 12px 16px;
            background: var(--color--surface-2-bg);
            border: 1px solid var(--color--border);
            border-radius: 6px;
            font-size: 0.875rem;
        }

        .referencing-list {
            display: flex;
            flex-wrap: wrap;
            gap: 8px;
            margin-top: 6px;
        }

        .referencing-name {
            padding: 2px 8px;
            background: var(--color--surface-bg);
            border: 1px solid var(--color--border);
            border-radius: 4px;
            color: var(--color--text);
            font-size: 0.875rem;
        }

        .form-appearance-section {
            border: 1px solid var(--color--border);
            border-radius: 6px;
            padding: 16px;
            background-color: var(--color--page-bg);
        }

        .form-title-color-controls {
            display: flex;
            align-items: center;
            gap: 8px;
        }

        .form-title-color-swatch {
            width: 40px;
            height: 32px;
            padding: 0;
            border: 1px solid var(--color--border);
            border-radius: 4px;
            cursor: pointer;
        }

        .form-title-color-text {
            flex: 1;
            max-width: 120px;
        }
    `,
    encapsulation: ViewEncapsulation.Emulated,
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false,
})
export class IngestionAdminFormComponent implements OnInit {
    @Input() ingestionPointId: number;
    @Output() onSave = new EventEmitter<void>();
    @Output() onCancel = new EventEmitter<void>();

    @ViewChild(IngestionAdminAccessRulesComponent)
    private accessRulesPanel: IngestionAdminAccessRulesComponent | undefined;

    @ViewChild("fieldsTab")
    private fieldsTab: IngestionAdminFormFieldsTabComponent | undefined;

    @ViewChild("formHeaderImage")
    private formHeaderImage?: S25FilestoreImageComponent;

    static readonly MAX_OPTION_LENGTH = 128;

    static readonly EVENT_CREATION_SUGGESTIONS: SuggestedField[] = [
        { label: "Event Name", fieldType: "TEXT" },
        { label: "Event Type", fieldType: "EVENT_TYPE" },
        { label: "Primary Organization", fieldType: "ORGANIZATION" },
        { label: "Occurrences", fieldType: "OBJECT_OCCURRENCE" },
        { label: "25Live Event Form", fieldType: "LIVE_25_EVENT_FORM" },
        { label: "Registrants", fieldType: "REGISTRANTS" },
    ];

    static readonly REGISTRATION_SUGGESTIONS: SuggestedField[] = [
        { label: "First Name", fieldType: "TEXT" },
        { label: "Last Name", fieldType: "TEXT" },
        { label: "Email", fieldType: "TEXT" },
    ];

    static readonly EVENT_SURVEY_SUGGESTIONS: SuggestedField[] = [
        { label: "Overall Rating", fieldType: "STARS_RATING" },
        { label: "Comments", fieldType: "TEXT" },
    ];

    static readonly ORG_JOIN_SUGGESTIONS: SuggestedField[] = [
        { label: "Organization", fieldType: "ORGANIZATION" },
        { label: "Contact Role", fieldType: "ORG_CONTACT_ROLE" },
        { label: "First Name", fieldType: "TEXT" },
        { label: "Last Name", fieldType: "TEXT" },
    ];

    static readonly ORG_CREATION_SUGGESTIONS: SuggestedField[] = [
        { label: "Organization Name", fieldType: "TEXT" },
        { label: "Organization Type", fieldType: "ORG_TYPE" },
        { label: "Primary Contact", fieldType: "CONTACT" },
        { label: "Categories", fieldType: "ORG_CATEGORIES" },
    ];

    readonly MAX_OPTION_LENGTH = IngestionAdminFormComponent.MAX_OPTION_LENGTH;
    readonly parseObjectOccurrenceFieldConfig = parseObjectOccurrenceFieldConfig;
    readonly parseCategoriesFieldConfig = parseCategoriesFieldConfig;
    readonly parseSectionFieldConfig = parseSectionFieldConfig;
    readonly FORM_FONT_SIZE_OPTIONS = FORM_FONT_SIZE_OPTIONS;
    readonly S25Util = S25Util;
    readonly DEFAULT_SECTION_HEADING_COLOR = DEFAULT_SECTION_HEADING_COLOR;
    readonly DEFAULT_FORM_TITLE_COLOR = DEFAULT_FORM_TITLE_COLOR;

    /** Display names for election ids (from list + selections); used by the elections config dropdown. */
    private electionNameById = new Map<number, string>();

    readonly parseElectionPositionFieldConfig = parseElectionPositionFieldConfig;
    readonly parseLive25EventFormFieldConfig = parseLive25EventFormFieldConfig;

    /** Available 25Live event form configurations for the LIVE_25_EVENT_FORM optional override dropdown. */
    readonly eventFormConfigs = s25Resource({
        loader: async () => {
            const [configs, error] = await S25Util.Maybe(EventCreationFormService.getAllConfigs());
            if (error) {
                console.error("Failed to load event form configs:", error);
                return [] as EventFormConfigData[];
            }
            return (configs as EventFormConfigData[]) ?? [];
        },
    });

    setLive25EventFormConfigurationId(
        fieldDef: IngestionFieldDefinition,
        eventFormConfigurationId: number | null,
    ): void {
        const idNum =
            eventFormConfigurationId === null || eventFormConfigurationId === undefined
                ? null
                : Number(eventFormConfigurationId);
        fieldDef.fieldConfig = JSON.stringify({
            eventFormConfigurationId: idNum && Number.isFinite(idNum) ? idNum : null,
        });
        this.cd.detectChanges();
    }

    getElectionPositionRestrictionFilter(electionId: number): string {
        return electionId ? String(electionId) : "";
    }

    setElectionPositionFieldConfig(
        fieldDef: IngestionFieldDefinition,
        positionsByElectionId: Record<string, number[]>,
    ): void {
        fieldDef.fieldConfig = serializeElectionPositionFieldConfig({ positionsByElectionId });
        this.cd.detectChanges();
    }

    getElectionPositionRestrictionRows(fieldDef: IngestionFieldDefinition): { electionId: number }[] {
        const { positionsByElectionId } = parseElectionPositionFieldConfig(fieldDef);
        return Object.keys(positionsByElectionId).map((electionKey) => ({
            electionId: Number(electionKey),
        }));
    }

    getElectionPositionRestrictionElectionItem(electionId: number): DropDownItem | null {
        if (!electionId) {
            return null;
        }
        const name = this.electionNameById.get(electionId) || `Election ${electionId}`;
        return {
            itemId: electionId,
            itemName: name,
            txt: name,
            val: String(electionId),
        } as DropDownItem;
    }

    addElectionPositionRestrictionRowFactory(fieldDef: IngestionFieldDefinition) {
        return () => this.addElectionPositionRestrictionRow(fieldDef);
    }

    addElectionPositionRestrictionRow(fieldDef: IngestionFieldDefinition): void {
        const config = parseElectionPositionFieldConfig(fieldDef);
        const placeholderElectionId = -Date.now();
        config.positionsByElectionId[String(placeholderElectionId)] = [];
        this.setElectionPositionFieldConfig(fieldDef, config.positionsByElectionId);
    }

    removeElectionPositionRestrictionRowFactory(fieldDef: IngestionFieldDefinition, electionId: number) {
        return () => this.removeElectionPositionRestrictionRow(fieldDef, electionId);
    }

    removeElectionPositionRestrictionRow(fieldDef: IngestionFieldDefinition, electionId: number): void {
        const config = parseElectionPositionFieldConfig(fieldDef);
        delete config.positionsByElectionId[String(electionId)];
        this.setElectionPositionFieldConfig(fieldDef, config.positionsByElectionId);
        this.cd.detectChanges();
    }

    onElectionPositionRestrictionElectionChange(
        fieldDef: IngestionFieldDefinition,
        previousElectionId: number,
        item: DropDownItem | null,
    ): void {
        const config = parseElectionPositionFieldConfig(fieldDef);
        const previousKey = String(previousElectionId);
        const positionIds = config.positionsByElectionId[previousKey] ?? [];
        delete config.positionsByElectionId[previousKey];

        const newElectionId = item?.itemId != null ? Number(item.itemId) : NaN;
        if (!Number.isFinite(newElectionId)) {
            this.setElectionPositionFieldConfig(fieldDef, config.positionsByElectionId);
            return;
        }
        if (item) {
            this.electionNameById.set(newElectionId, (item.itemName || item.txt || `Election ${newElectionId}`).trim());
        }
        config.positionsByElectionId[String(newElectionId)] = positionIds;
        this.setElectionPositionFieldConfig(fieldDef, config.positionsByElectionId);
    }

    getElectionPositionRestrictionSelectedItems(
        fieldDef: IngestionFieldDefinition,
        electionId: number,
    ): DropDownItem[] {
        const { positionsByElectionId } = parseElectionPositionFieldConfig(fieldDef);
        const positionIds = positionsByElectionId[String(electionId)] ?? [];
        return positionIds.map(
            (positionId): DropDownItem => ({
                itemId: positionId,
                itemName: `Position ${positionId}`,
            }),
        );
    }

    onElectionPositionRestrictionPositionsChange(
        fieldDef: IngestionFieldDefinition,
        electionId: number,
        items: DropDownItem[],
    ): void {
        const config = parseElectionPositionFieldConfig(fieldDef);
        const positionIds = (items ?? [])
            .map((item) => Number(item.itemId))
            .filter((positionId) => Number.isFinite(positionId) && positionId > 0);
        config.positionsByElectionId[String(electionId)] = positionIds;
        this.setElectionPositionFieldConfig(fieldDef, config.positionsByElectionId);
    }

    private async hydrateElectionNamesForFieldConfig(fieldDefs: IngestionFieldDefinition[]) {
        this.electionNameById.clear();
        let list: Awaited<ReturnType<typeof ElectionApi.listElections>> = [];
        try {
            list = await ElectionApi.listElections();
        } catch (e) {
            console.error("Failed to list elections for ingestion field admin:", e);
            return;
        }
        for (const e of S25Util.array.forceArray(list)) {
            if (e.electionId && !e.deleted) {
                this.electionNameById.set(e.electionId, (e.name && e.name.trim()) || `Election ${e.electionId}`);
            }
        }
        for (const fd of fieldDefs) {
            if (fd.fieldType !== "ELECTION_POSITION") {
                continue;
            }
            for (const electionId of getElectionIdsFromFieldConfig(fd)) {
                if (!this.electionNameById.has(electionId)) {
                    this.electionNameById.set(electionId, `Election ${electionId}`);
                }
            }
        }
    }

    readonly categoriesFieldConfigAdminSelection = new WeakMap<IngestionFieldDefinition, S25ItemI[]>();

    // Expose AnswerType enum to template
    AnswerType = AnswerType;

    ingestionPoint: IngestionPoint;
    fieldDefinitions: IngestionFieldDefinition[];
    init = false;

    // Workflow processes
    readonly availableWorkflows = signal<Workflow.WorkflowProcess[]>([]);
    referencingWorkflows: Workflow.WorkflowProcess[] = [];
    workflowAssignmentType: "none" | "direct" | "default" = "none";

    readonly selectedWorkflowId = signal<Nullable<number>>(undefined);

    readonly selectedWorkflowProcess = s25Resource({
        if: () => !!this.selectedWorkflowId(),
        params: () => this.selectedWorkflowId()!,
        loader: ({ params: id }) => WorkflowService.getWorkflowProcess(id),
    });

    readonly missingFormContextAction = computed(() => {
        if (this.workflowAssignmentType !== "direct" || !this.selectedWorkflowId()) return false;
        const process = this.selectedWorkflowProcess.value();
        if (!process) return false;
        return !process.stages?.some(
            (stage) =>
                !!stage.actions?.some(
                    (a) =>
                        a.actionItemTypeId === INGESTION_POINT_TYPE_ID &&
                        a.actionItemId === this.ingestionPoint?.ingestionPointId,
                ),
        );
    });

    readonly selectedDefaultWorkflowId = signal<Nullable<number>>(undefined);

    readonly selectedDefaultWorkflowProcess = s25Resource({
        if: () => !!this.selectedDefaultWorkflowId(),
        params: () => this.selectedDefaultWorkflowId()!,
        loader: ({ params: id }) => WorkflowService.getWorkflowProcess(id),
    });

    readonly missingDefaultFormContextAction = computed(() => {
        if (this.workflowAssignmentType !== "default" || !this.selectedDefaultWorkflowId()) return false;
        const process = this.selectedDefaultWorkflowProcess.value();
        if (!process) return false;
        return !process.stages?.some(
            (stage) =>
                !!stage.actions?.some(
                    (a) =>
                        a.actionItemTypeId === INGESTION_POINT_TYPE_ID &&
                        a.actionItemId === this.ingestionPoint?.ingestionPointId,
                ),
        );
    });

    readonly directWorkflowDropdownItems = computed(() =>
        buildWorkflowDropdownItems(
            this.availableWorkflows(),
            this.selectedWorkflowId(),
            this.selectedWorkflowProcess.value()?.workflowProcessName,
        ),
    );

    readonly defaultWorkflowDropdownItems = computed(() =>
        buildWorkflowDropdownItems(
            this.availableWorkflows(),
            this.selectedDefaultWorkflowId(),
            this.selectedDefaultWorkflowProcess.value()?.workflowProcessName,
        ),
    );

    readonly selectedDirectWorkflowItem = computed(() =>
        findSelectedWorkflowDropdownItem(this.directWorkflowDropdownItems(), this.selectedWorkflowId()),
    );

    readonly selectedDefaultWorkflowItem = computed(() =>
        findSelectedWorkflowDropdownItem(this.defaultWorkflowDropdownItems(), this.selectedDefaultWorkflowId()),
    );

    @HostBinding("class.fields-palette-gutter-open")
    get fieldsPaletteGutterOpenClass(): boolean {
        return this.activeTab() === "fields" && (this.fieldsTab?.fieldsPaletteOpen() ?? false);
    }

    readonly EVENT_CREATION_SUGGESTIONS = IngestionAdminFormComponent.EVENT_CREATION_SUGGESTIONS;
    readonly REGISTRATION_SUGGESTIONS = IngestionAdminFormComponent.REGISTRATION_SUGGESTIONS;
    readonly EVENT_SURVEY_SUGGESTIONS = IngestionAdminFormComponent.EVENT_SURVEY_SUGGESTIONS;
    readonly ORG_CREATION_SUGGESTIONS = IngestionAdminFormComponent.ORG_CREATION_SUGGESTIONS;
    readonly ORG_JOIN_SUGGESTIONS = IngestionAdminFormComponent.ORG_JOIN_SUGGESTIONS;

    // Reporting field codes for dropdown
    reportingFieldCodes: string[] = [];

    // Custom attributes (primitive types only) for CUSTOM_ATTRIBUTE field type
    eventCustomAttributes = signal<{ itemId: number; itemName?: string; itemTypeId: string | number }[]>([]);
    orgCustomAttributes = signal<{ itemId: number; itemName?: string; itemTypeId: string | number }[]>([]);

    readonly activeTab = signal<FormBuilderTabId>("fields");
    readonly previewRemountKey = signal(0);
    readonly previewFieldDefinitionsClone = signal<IngestionFieldDefinition[]>([]);
    readonly previewShowHideRules = signal<Rule[]>([]);

    readonly formBuilderTabs: ReadonlyArray<{ id: FormBuilderTabId; label: string }> = [
        { id: "settings", label: "Settings" },
        { id: "workflow", label: "Workflow" },
        { id: "access", label: "Access" },
        { id: "fields", label: "Fields" },
        { id: "rules", label: "Rules" },
        { id: "preview", label: "Preview" },
    ];

    private readonly securityService = inject(INGESTION_SECURITY_SERVICE);

    readonly starsNumericScaleMin = signal(STARS_NUMERIC_SCALE_MIN);
    readonly starsNumericScaleMax = signal(STARS_NUMERIC_SCALE_MAX);

    private readonly elementRef = inject(ElementRef);
    private readonly itemService = inject(ItemService);

    readonly searchCriteriaDataUtil = inject(SearchCriteriaDataUtil);

    constructor(public cd: ChangeDetectorRef) {}

    canCreateForm(): boolean {
        return this.securityService.canCreateForm(this.ingestionPoint?.category, this.ingestionPoint?.subCat);
    }

    getFormBuilderTabs(): ReadonlyArray<{ id: FormBuilderTabId; label: string }> {
        if (this.canCreateForm()) {
            return this.formBuilderTabs;
        }
        return this.formBuilderTabs.filter((tab) => tab.id !== "rules");
    }

    onStarsNumericScaleRangeChange = (
        fieldDef: IngestionFieldDefinition,
        bound: "min" | "max",
        value: number | string | undefined,
    ) => {
        const minLimit = this.starsNumericScaleMin();
        const maxLimit = this.starsNumericScaleMax();
        let num: number | undefined =
            value === undefined || value === null ? undefined : typeof value === "string" ? parseInt(value, 10) : value;
        if (num === undefined || Number.isNaN(num)) {
            if (bound === "min") {
                num = minLimit;
            } else {
                num = maxLimit;
            }
        }
        num = Math.round(S25Util.clamp(num, minLimit, maxLimit));
        setTimeout(() => {
            if (bound === "min") {
                fieldDef.minLength = num;
                if (fieldDef.maxLength != null && fieldDef.maxLength < num) {
                    fieldDef.maxLength = num;
                }
            } else {
                fieldDef.maxLength = num;
                if (fieldDef.minLength != null && fieldDef.minLength > num) {
                    fieldDef.minLength = num;
                }
            }
            this.cd.detectChanges();
        }, 0);
    };

    onTextMinMaxChange = (
        fieldDef: IngestionFieldDefinition,
        bound: "min" | "max",
        value: number | string | undefined,
    ) => {
        const isEmpty = value === "" || value === undefined || value === null;
        let num: number | undefined = isEmpty
            ? undefined
            : typeof value === "string"
              ? parseInt(value, 10)
              : (value as number);
        if (num !== undefined && (Number.isNaN(num) || num < 0)) {
            if (bound === "min") {
                num = 0;
            } else {
                num = undefined;
            }
        }
        if (num !== undefined) {
            num = Math.max(0, Math.round(num));
        }
        setTimeout(() => {
            if (bound === "min") {
                fieldDef.minLength = num;
                if (num !== undefined && fieldDef.maxLength != null && fieldDef.maxLength < num) {
                    fieldDef.maxLength = num;
                }
            } else {
                fieldDef.maxLength = num;
                if (num !== undefined && fieldDef.minLength != null && fieldDef.minLength > num) {
                    fieldDef.minLength = num;
                }
            }
            this.cd.detectChanges();
        }, 0);
    };

    getFieldTypeDisplayLabel = (fieldType: IngestionFieldDefinition["fieldType"] | undefined): string => {
        if (!fieldType) {
            return "Unknown";
        }
        return getPaletteTileLabel(fieldType) ?? fieldType;
    };

    onFieldTypeChange = (fieldDef: IngestionFieldDefinition) => {
        if (!fieldDef?.fieldType) return;

        if (
            (fieldDef.fieldDefinitionId ?? 0) >= 0 &&
            !fieldDef.isDeleted &&
            isAuthlessRestrictedFieldType(fieldDef.fieldType) &&
            this.ingestionPoint.authless
        ) {
            S25Util.showError(
                `${INGESTION_AUTHLESS_RESTRICTED_FIELD_TYPES.join(", ")} fields cannot be used in authless forms. Please disable 'Allow authless submissions' first.`,
            );
            // Reset to a default field type after the current change cycle
            setTimeout(() => {
                fieldDef.fieldType = "SELECT";
                fieldDef.answerType = AnswerType.STRING;
                this.cd.detectChanges();
            }, 0);
            return;
        }

        // Save values that STARS_RATING / NUMERIC_SCALE may need before clearing
        const prevMinLength = fieldDef.minLength;
        const prevMaxLength = fieldDef.maxLength;

        // Clear all configurable fields first, then set type-specific values below
        fieldDef.options = [];
        fieldDef.minLength = undefined;
        fieldDef.maxLength = undefined;
        fieldDef.regexValid = undefined;

        switch (true) {
            case fieldDef.fieldType === "SECTION":
                fieldDef.answerType = undefined;
                fieldDef.required = false;
                break;
            case fieldDef.fieldType === "FILE":
                fieldDef.answerType = AnswerType.FILE;
                break;
            case fieldDef.fieldType === "IMAGE":
                fieldDef.answerType = AnswerType.LARGE_STRING;
                break;
            case this.isOptionBasedFieldType(fieldDef.fieldType):
                if (!fieldDef.answerType || fieldDef.answerType === AnswerType.FILE) {
                    fieldDef.answerType = AnswerType.STRING;
                }
                if (fieldDef.answerType === AnswerType.BOOLEAN) {
                    this.ensureBooleanOptions(fieldDef);
                }
                break;
            case [
                "EVENT_TYPE",
                "ORGANIZATION",
                "CONTACT",
                "ROLE",
                "ORG_TYPE",
                "ORG_RATING",
                "SECURITY_GROUP",
                "FORM",
                "EVENT",
                "CATEGORIES",
                "REQUIREMENTS",
                "ORGANIZATION_MULTISELECT",
                "ORG_CATEGORIES",
                "LIVE_25_EVENT_FORM",
            ].includes(fieldDef.fieldType):
                fieldDef.answerType = AnswerType.INTEGER;
                if (fieldDef.fieldType === "CATEGORIES" || fieldDef.fieldType === "ORG_CATEGORIES") {
                    fieldDef.fieldConfig = stringifyCategoriesFieldConfig({
                        categoryIds: [],
                        singleSelect: false,
                        expandAutomatically: false,
                    });
                    this.categoriesFieldConfigAdminSelection.set(fieldDef, []);
                }
                break;
            case fieldDef.fieldType === "CUSTOM_ATTRIBUTE":
            case fieldDef.fieldType === "ORG_CUSTOM_ATTRIBUTE":
                fieldDef.answerType = AnswerType.VARIANT;
                break;
            case fieldDef.fieldType === "TEXT":
                fieldDef.answerType = AnswerType.STRING;
                break;
            case fieldDef.fieldType === "RICH_TEXT":
                fieldDef.answerType = AnswerType.LARGE_STRING;
                break;
            case fieldDef.fieldType === "ELECTION_POSITION":
                fieldDef.answerType = AnswerType.INTEGER;
                fieldDef.fieldConfig = serializeElectionPositionFieldConfig({ positionsByElectionId: {} });
                break;
            case fieldDef.fieldType === "STARS_RATING":
            case fieldDef.fieldType === "NUMERIC_SCALE": {
                fieldDef.answerType = AnswerType.INTEGER;
                const minLimit = this.starsNumericScaleMin();
                const maxLimit = this.starsNumericScaleMax();
                fieldDef.minLength = S25Util.clamp(prevMinLength ?? minLimit, minLimit, maxLimit);
                fieldDef.maxLength = S25Util.clamp(prevMaxLength ?? 5, minLimit, maxLimit);
                break;
            }
            case fieldDef.fieldType === "DURATION":
                fieldDef.answerType = AnswerType.DATETIME;
                break;
            case fieldDef.fieldType === "TOGGLE":
                fieldDef.answerType = AnswerType.BOOLEAN;
                fieldDef.options = [
                    { option: "Yes", fieldDefinitionId: fieldDef.fieldDefinitionId },
                    { option: "No", fieldDefinitionId: fieldDef.fieldDefinitionId },
                ];
                break;
            case this.isJsonField(fieldDef):
                fieldDef.answerType = AnswerType.JSON;
                break;
        }
        this.cd.detectChanges();
    };

    onAnswerTypeChange = (fieldDef: IngestionFieldDefinition) => {
        // Prevent changing answer type for FILE fields
        if (fieldDef.fieldType === "FILE") {
            fieldDef.answerType = AnswerType.FILE;
            return;
        }
        if (fieldDef.fieldType === "RICH_TEXT") {
            fieldDef.answerType = AnswerType.LARGE_STRING;
            return;
        }
        if (fieldDef.fieldType === "IMAGE") {
            fieldDef.answerType = AnswerType.LARGE_STRING;
            return;
        }

        // Handle answer type changes
        if (this.isOptionBasedFieldType(fieldDef.fieldType)) {
            if (fieldDef.answerType === AnswerType.BOOLEAN) {
                // Auto-populate and lock boolean options
                this.ensureBooleanOptions(fieldDef);
            } else {
                // Clear options when changing away from BOOLEAN
                fieldDef.options = [];
            }
        }
        this.cd.detectChanges();
    };

    onSubCategoryChange = (subCategory: string) => {
        this.ingestionPoint.subCat = subCategory;
        this.cd.detectChanges();
    };

    onAuthlessChange = (authless: boolean) => {
        // If enabling authless, check if there are restricted field types
        if (authless && this.fieldDefinitions) {
            const restrictedFields = this.fieldDefinitions.filter(
                (fieldDef) =>
                    (fieldDef.fieldDefinitionId ?? 0) >= 0 &&
                    !fieldDef.isDeleted &&
                    isAuthlessRestrictedFieldType(fieldDef.fieldType || ""),
            );
            if (restrictedFields.length > 0) {
                S25Util.showError(
                    `Cannot enable authless submissions: This form contains ${restrictedFields.length} field(s) that are not allowed in authless forms (${INGESTION_AUTHLESS_RESTRICTED_FIELD_TYPES.join(", ")}). Please remove these fields first or keep authless submissions disabled.`,
                );
                // Revert the change - use setTimeout to ensure checkbox updates after current change cycle
                setTimeout(() => {
                    this.ingestionPoint.authless = false;
                    this.cd.detectChanges();
                }, 0);
                return;
            }
        }
        this.cd.detectChanges();
    };

    addOption = (fieldDef: IngestionFieldDefinition) => {
        // Prevent adding options for boolean fields
        if (fieldDef.answerType === AnswerType.BOOLEAN && this.isOptionBasedFieldType(fieldDef.fieldType)) {
            return;
        }

        const option = fieldDef.newAnswer?.text;
        if (option) {
            // Validate option length before adding
            if (!this.isValidOptionLength(option)) {
                S25Util.showError(`Option cannot exceed ${IngestionAdminFormComponent.MAX_OPTION_LENGTH} characters`);
                return;
            }
            if (fieldDef.newAnswer) fieldDef.newAnswer.text = "";
            if (!fieldDef.options) fieldDef.options = [];
            fieldDef.options.push({
                option: option,
                fieldDefinitionId: fieldDef.fieldDefinitionId,
                sortOrder: fieldDef.options.length + 1,
            });
        }
        this.fieldsTab?.notifyFieldDefinitionsChanged();
    };

    removeOption = (fieldDef: IngestionFieldDefinition, option: { option?: string }) => {
        // Prevent removing options for boolean fields
        if (fieldDef.answerType === AnswerType.BOOLEAN && this.isOptionBasedFieldType(fieldDef.fieldType)) {
            return;
        }

        if (!fieldDef.options) return;
        for (let i = fieldDef.options.length - 1; i >= 0; i--) {
            if (fieldDef.options[i].option === option.option) {
                fieldDef.options.splice(i, 1);
            }
        }
        this.fieldsTab?.notifyFieldDefinitionsChanged();
    };

    onToggleOptionLabelChange = (fieldDef: IngestionFieldDefinition, optionIndex: 0 | 1, value: string): void => {
        if (fieldDef.fieldType !== "TOGGLE") {
            return;
        }
        if (!fieldDef.options?.length || fieldDef.options.length < 2) {
            fieldDef.options = [
                { option: fieldDef.options?.[0]?.option ?? "Yes", fieldDefinitionId: fieldDef.fieldDefinitionId },
                { option: fieldDef.options?.[1]?.option ?? "No", fieldDefinitionId: fieldDef.fieldDefinitionId },
            ];
        }
        fieldDef.options[optionIndex].option = value;
    };

    addFieldFromType = (fieldType: IngestionFieldDefinition["fieldType"], insertIndex?: number, label?: string) => {
        if (!fieldType) {
            return;
        }

        const fieldLabel = label ?? getPaletteTileLabel(fieldType) ?? "New Field! Remember to Save When Finished.";
        const newField: IngestionFieldDefinition = {
            fieldLabel,
            fieldType,
            answerType: AnswerType.STRING,
            options: [],
            newAnswer: { text: "" },
            required: false,
            hidden: false,
            derived: false,
            requiresReapproval: true,
            infoTextExpanded: false,
        };
        newField.reportingFieldCode = this.generateReportingCode(newField.fieldLabel!);
        newField.prevFieldLabel = newField.fieldLabel;
        this.onFieldTypeChange(newField);

        if (insertIndex == null || insertIndex >= this.fieldDefinitions.length) {
            this.fieldDefinitions.push(newField);
        } else {
            this.fieldDefinitions.splice(insertIndex, 0, newField);
        }

        this.syncFieldSortOrder();
        this.fieldsTab?.notifyFieldDefinitionsChanged();
        this.cd.markForCheck();

        setTimeout(() => {
            const index =
                insertIndex == null || insertIndex >= this.fieldDefinitions.length
                    ? this.fieldDefinitions.length - 1
                    : insertIndex;
            const element = this.elementRef.nativeElement.querySelector(`[data-field-idx="${index}"]`);
            element?.scrollIntoView({ behavior: "smooth", block: "start" });
        });
    };

    syncFieldSortOrder = () => {
        for (let index = 0; index < this.fieldDefinitions.length; index++) {
            this.fieldDefinitions[index].sortOrder = index;
        }
    };

    onFieldDefinitionsOrderChanged = () => {
        this.syncFieldSortOrder();
        this.fieldsTab?.notifyFieldDefinitionsChanged();
        this.cd.markForCheck();
    };

    removeFieldDefinition = (fieldDef: IngestionFieldDefinition) => {
        this.fieldDefinitions.splice(this.fieldDefinitions.indexOf(fieldDef), 1);
        this.syncFieldSortOrder();
        this.fieldsTab?.notifyFieldDefinitionsChanged();
        this.cd.markForCheck();
    };

    isValidRegex = (pattern: string | undefined): boolean => {
        if (!pattern || !pattern.trim()) {
            return true; // Empty patterns are considered valid (optional)
        }

        try {
            new RegExp(pattern);
            return true;
        } catch (error) {
            return false;
        }
    };

    validateRegexPatterns = (): string[] => {
        const errors: string[] = [];

        this.fieldDefinitions.forEach((fieldDef, index) => {
            if (fieldDef.fieldType === "TEXT" && fieldDef.regexValid && fieldDef.regexValid.trim()) {
                if (!this.isValidRegex(fieldDef.regexValid)) {
                    const fieldLabel = fieldDef.fieldLabel || `Field ${index + 1}`;
                    errors.push(`${fieldLabel}: Invalid regex pattern "${fieldDef.regexValid}"`);
                }
            }
        });

        return errors;
    };

    isValidReportingCode = (code: string | undefined): boolean => {
        if (!code || !code.trim()) {
            return true; // Empty codes are considered valid (optional)
        }
        // Only alphanumeric characters, max 32 characters
        return /^[A-Za-z0-9]{1,32}$/.test(code);
    };

    isValidOptionLength = (option: string | undefined): boolean => {
        if (!option) {
            return true; // Empty options are considered valid (optional)
        }
        return option.length <= IngestionAdminFormComponent.MAX_OPTION_LENGTH;
    };

    generateReportingCode = (fieldLabel: string): string => {
        if (!fieldLabel || !fieldLabel.trim()) {
            return "";
        }

        // Remove all non-alphanumeric characters and convert to camelCase
        const code = fieldLabel
            .replace(/[^A-Za-z0-9\s]/g, "") // Remove special characters but keep spaces
            .split(/\s+/) // Split by whitespace
            .filter((word) => word.length > 0) // Remove empty strings
            .map((word, index) => {
                if (index === 0) {
                    return word.toLowerCase(); // First word lowercase
                } else {
                    return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase(); // Subsequent words capitalized
                }
            })
            .join(""); // Join without spaces

        // Truncate to 32 characters max
        return code.substring(0, 32);
    };

    onFieldLabelChange = (fieldDef: IngestionFieldDefinition) => {
        // Auto-generate reporting code if it's empty or was previously auto-generated
        if (
            !fieldDef.reportingFieldCode ||
            fieldDef.reportingFieldCode === this.generateReportingCode(fieldDef.prevFieldLabel || "")
        ) {
            fieldDef.reportingFieldCode = this.generateReportingCode(fieldDef.fieldLabel || "");
        }
        // Update the previous field label for next comparison
        fieldDef.prevFieldLabel = fieldDef.fieldLabel;
        this.cd.detectChanges();
    };

    onReportingCodeChange = (fieldDef: IngestionFieldDefinition) => {
        // Ensure the code is valid
        if (fieldDef.reportingFieldCode && !this.isValidReportingCode(fieldDef.reportingFieldCode)) {
            // Don't prevent typing, but the validation will show an error
        }
        this.cd.detectChanges();
    };

    validateReportingCodes = (): string[] => {
        const errors: string[] = [];

        this.fieldDefinitions.forEach((fieldDef, index) => {
            if (this.isSystemField(fieldDef)) {
                return;
            }
            if (fieldDef.reportingFieldCode && fieldDef.reportingFieldCode.trim()) {
                if (!this.isValidReportingCode(fieldDef.reportingFieldCode)) {
                    const fieldLabel = fieldDef.fieldLabel || `Field ${index + 1}`;
                    errors.push(
                        `${fieldLabel}: Invalid reporting code "${fieldDef.reportingFieldCode}" - only alphanumeric characters allowed, max 32 characters`,
                    );
                }
            }
        });

        return errors;
    };

    refresh = async () => {
        const resp = await S25Util.all({
            ingestionPoint: DataIngestionService.getIngestionPoint(this.ingestionPointId),
            fieldDefinitions: DataIngestionService.getFieldDefinitions(this.ingestionPointId),
            eventCustomAttributes: SearchCriteriaService.getCustomAttributeItems(1, true),
            orgCustomAttributes: SearchCriteriaService.getOrganizationCustomAttributes(),
        });

        if (!resp.ingestionPoint) {
            S25Util.showError("Form not found");
            return;
        }

        this.ingestionPoint = resp.ingestionPoint;
        // Ensure use-object exists
        if (!this.ingestionPoint.use) {
            this.ingestionPoint.use = {
                registration: false,
                eventSurvey: false,
                eventCreation: false,
                orgCreation: false,
                orgEdit: false,
                orgJoin: false,
                eventEdit: false,
                electionCandidate: false,
            };
        }
        // Ensure authless defaults to false if undefined
        if (this.ingestionPoint.authless === undefined) {
            this.ingestionPoint.authless = false;
        }
        if (this.ingestionPoint.allowEditAfterSubmit === undefined) {
            this.ingestionPoint.allowEditAfterSubmit = false;
        }
        this.determineWorkflowAssignmentType();
        this.selectedWorkflowId.set(this.ingestionPoint.workflowId ?? undefined);
        this.selectedDefaultWorkflowId.set(this.ingestionPoint.defaultWorkflowId ?? undefined);

        this.initializeFieldDefinitionsForAdmin(resp.fieldDefinitions);
        this.fieldDefinitions = resp.fieldDefinitions;

        await this.hydrateCategoriesFieldConfigAdminSelections(this.fieldDefinitions);
        await this.hydrateElectionNamesForFieldConfig(this.fieldDefinitions);

        // Filter event custom attributes to primitive types only
        const filtered = (resp.eventCustomAttributes || []).filter(
            (item) => AnswerType.fromAtrbDataType(String(item.itemTypeId)) != null,
        );
        this.eventCustomAttributes.set(filtered);

        const filteredOrg = (resp.orgCustomAttributes || []).filter(
            (item) => AnswerType.fromAtrbDataType(String(item.itemTypeId)) != null,
        );
        this.orgCustomAttributes.set(filteredOrg);
        this.stampMissingCustomAttributeTypes(this.fieldDefinitions);

        // Load names for ORGANIZATION and CONTACT default values
        await this.loadDefaultValueNames();

        this.init = true;
        this.cd.detectChanges();
        this.fieldsTab?.resetFieldsTabState();
    };

    openDownloadQrModal = async () => {
        const pointId = this.ingestionPoint?.ingestionPointId;
        if (!pointId) {
            S25Util.showError("Save the form before downloading a QR code.");
            return;
        }
        await ModalService.modal("ingestion-form-qr", {
            title: `QR Code: ${this.ingestionPoint.ingestionPointName ?? "Form"}`,
            ingestionPointId: pointId,
            formName: this.ingestionPoint.ingestionPointName,
        });
    };

    saveAll = async () => {
        // Validate form name
        if (!this.ingestionPoint.ingestionPointName?.trim()) {
            S25Util.showError("Form name is required");
            return;
        }

        if (!this.ingestionPoint.category) {
            S25Util.showError("Category is required");
            return;
        }

        // Validate that FILE, EVENT_TYPE, ORGANIZATION, OBJECT_OCCURRENCE fields are not used in authless forms
        if (this.ingestionPoint.authless) {
            const restrictedFields = this.fieldDefinitions.filter(
                (fieldDef) =>
                    (fieldDef.fieldDefinitionId ?? 0) >= 0 &&
                    !fieldDef.isDeleted &&
                    isAuthlessRestrictedFieldType(fieldDef.fieldType || ""),
            );
            if (restrictedFields.length > 0) {
                S25Util.showError(
                    `${INGESTION_AUTHLESS_RESTRICTED_FIELD_TYPES.join(", ")} fields cannot be used in authless forms. Please disable 'Allow authless submissions' or remove these fields.`,
                );
                return;
            }
        }

        // Validate all regex patterns before saving
        const validationErrors = this.validateRegexPatterns();
        if (validationErrors.length > 0) {
            S25Util.showError("Invalid regex patterns found:\n" + validationErrors.join("\n"));
            return;
        }

        // Validate all reporting codes before saving
        const reportingCodeErrors = this.validateReportingCodes();
        if (reportingCodeErrors.length > 0) {
            S25Util.showError("Invalid reporting codes found:\n" + reportingCodeErrors.join("\n"));
            return;
        }

        // Validate all default values before saving
        const defaultValueErrors = this.validateDefaultValues();
        if (defaultValueErrors.length > 0) {
            S25Util.showError("Invalid default values found:\n" + defaultValueErrors.join("\n"));
            return;
        }

        // Validate all option lengths before saving
        const optionLengthErrors = this.validateOptionLengths();
        if (optionLengthErrors.length > 0) {
            S25Util.showError("Invalid option lengths found:\n" + optionLengthErrors.join("\n"));
            return;
        }

        // Validate min <= max for any field with both set
        const minMaxErrors = this.validateMinMaxRanges();
        if (minMaxErrors.length > 0) {
            S25Util.showError("Invalid min/max ranges:\n" + minMaxErrors.join("\n"));
            return;
        }

        if (this.workflowAssignmentType === "none" && this.referencingWorkflows.length) {
            const names = this.referencingWorkflows.map((w) => w.workflowProcessName).join(", ");
            const proceed = await AlertService.confirm(
                `This form is referenced by workflow(s): ${names}, but has no workflow assignment configured. Do you want to save anyway?`,
            );
            if (!proceed) return;
        }

        if (this.missingFormContextAction() || this.missingDefaultFormContextAction()) {
            const configureNow = await AlertService.confirm(
                "The assigned workflow has no action referencing this form as its form context. Configure actions now? (Choose No to save the form without adding actions.)",
            );
            if (configureNow) {
                const persisted = await this.persistFormAndReloadFields();
                if (!persisted) return;
                await this.openAddWorkflowActionsModal();
                this.onSave.emit();
                return;
            }
        }

        const saved = await this.persistFormAndReloadFields();
        if (saved) {
            this.onSave.emit();
        }
    };

    onEventCustomAttributePicked(
        fieldDef: IngestionFieldDefinition,
        picked: { val?: number; custAtrbType?: string | number; multi_val?: boolean } | undefined,
    ): void {
        stampCustAtrbFromSearchItem(
            fieldDef,
            picked?.val != null
                ? { itemId: picked.val, itemTypeId: picked.custAtrbType, multi_val: picked.multi_val }
                : undefined,
        );
        this.cd.detectChanges();
    }

    onOrgCustomAttributePicked(fieldDef: IngestionFieldDefinition, custAtrbId: number | undefined): void {
        const attr = this.orgCustomAttributes().find((item) => item.itemId === custAtrbId);
        stampCustAtrbFromSearchItem(fieldDef, attr);
        this.cd.detectChanges();
    }

    private initializeFieldDefinitionsForAdmin(fieldDefinitions: IngestionFieldDefinition[]): void {
        for (const fieldDef of fieldDefinitions) {
            fieldDef.infoTextExpanded = fieldDef.infoTextExpanded ?? false;
            fieldDef.newAnswer = { text: "" };
            if (!fieldDef.options) {
                fieldDef.options = [];
            }
            if (fieldDef.answerType === AnswerType.BOOLEAN && this.isOptionBasedFieldType(fieldDef.fieldType)) {
                this.ensureBooleanOptions(fieldDef);
            }
            if (fieldDef.fieldType === "TOGGLE" && (!fieldDef.options || fieldDef.options.length < 2)) {
                fieldDef.options = [
                    { option: fieldDef.options?.[0]?.option ?? "Yes", fieldDefinitionId: fieldDef.fieldDefinitionId },
                    { option: fieldDef.options?.[1]?.option ?? "No", fieldDefinitionId: fieldDef.fieldDefinitionId },
                ];
            }
            if (fieldDef.fieldType === "RICH_TEXT") {
                fieldDef.answerType = AnswerType.LARGE_STRING;
            }
            if (fieldDef.fieldType === "IMAGE") {
                fieldDef.answerType = AnswerType.LARGE_STRING;
            }
        }
        this.stampMissingCustomAttributeTypes(fieldDefinitions);
    }

    private stampMissingCustomAttributeTypes(fieldDefinitions: IngestionFieldDefinition[]): void {
        const items: CustAtrbSearchItem[] = [...this.eventCustomAttributes(), ...this.orgCustomAttributes()];
        stampMissingCustAtrbFromSearchItems(fieldDefinitions, items);
    }

    private async flushPendingSectionHeaderImages(): Promise<boolean> {
        const pendingImageError = await this.fieldsTab?.flushPendingSectionHeaderImages();
        if (pendingImageError) {
            S25Util.showError(pendingImageError);
            return false;
        }
        return true;
    }

    private async flushPendingFormHeaderImage(): Promise<boolean> {
        const filestoreImage = this.formHeaderImage;
        if (!filestoreImage) {
            return true;
        }
        await filestoreImage.guardedSave();
        const errorMessage = filestoreImage.errorMsg()?.trim();
        if (errorMessage) {
            S25Util.showError(errorMessage);
            return false;
        }
        return true;
    }

    private async persistFormAndReloadFields(): Promise<boolean> {
        try {
            if (!(await this.flushPendingSectionHeaderImages())) {
                return false;
            }
            if (!(await this.flushPendingFormHeaderImage())) {
                return false;
            }

            this.ingestionPoint.formStyle = stringifyFormStyleConfig(this.getFormStyleConfig());

            await DataIngestionService.updateIngestionPoint(this.ingestionPointId, this.ingestionPoint);

            // Then save field definitions
            await DataIngestionService.replaceFieldDefinitions(this.ingestionPointId, this.fieldDefinitions);

            // Reload reporting field codes to include any newly saved codes
            await this.loadReportingFieldCodes();

            const accessRulesSaved =
                this.accessRulesPanel != null ? await this.accessRulesPanel.persistAccessRules() : true;
            if (!accessRulesSaved) {
                S25Util.showError(
                    "Form and fields were saved, but access rules could not be saved. Switch to the Access tab, fix any issues, and click Save form again.",
                );
                this.cd.detectChanges();
                return true;
            }

            const refreshedPoint = await DataIngestionService.getIngestionPoint(this.ingestionPointId);
            if (refreshedPoint) {
                this.ingestionPoint = refreshedPoint;
            }
            const fieldDefs = await DataIngestionService.getFieldDefinitions(this.ingestionPointId);
            this.initializeFieldDefinitionsForAdmin(fieldDefs);
            await this.hydrateCategoriesFieldConfigAdminSelections(fieldDefs);
            await this.hydrateElectionNamesForFieldConfig(fieldDefs);
            this.fieldDefinitions = fieldDefs;
            invalidateIngestionFormRulesFieldCache(this.ingestionPointId, this.itemService);
            this.cd.detectChanges();
            return true;
        } catch (err) {
            S25Util.showError(err);
            return false;
        }
    }

    private async openAddWorkflowActionsModal(): Promise<void> {
        const workflowProcessId =
            this.workflowAssignmentType === "direct"
                ? this.ingestionPoint.workflowId
                : this.ingestionPoint.defaultWorkflowId;
        if (!workflowProcessId) return;

        const workflowProcess = await WorkflowService.getWorkflowProcess(Number(workflowProcessId));
        const modalData: WorkflowAddFormActionsModalData = {
            workflowProcessId: Number(workflowProcessId),
            ingestionPointId: this.ingestionPointId,
            ingestionPointName: this.ingestionPoint.ingestionPointName,
            fieldDefinitions: this.fieldDefinitions,
            workflowProcess,
        };

        const saved = await ModalService.modal("workflow-add-form-actions-modal", {
            title: "Add workflow actions for this form",
            ...modalData,
        });
        if (saved) {
            if (this.workflowAssignmentType === "direct") {
                await this.selectedWorkflowProcess.reloadAndAwait();
            } else if (this.workflowAssignmentType === "default") {
                await this.selectedDefaultWorkflowProcess.reloadAndAwait();
            }
            this.computeReferencingWorkflows();
            this.cd.detectChanges();
        }
    }

    cancel = () => {
        this.onCancel.emit();
    };

    setActiveTab = async (tab: FormBuilderTabId): Promise<void> => {
        if (tab !== "fields") {
            this.fieldsTab?.closeFieldsPalette();
        }
        if (tab === "preview") {
            try {
                await this.loadPreviewTab();
            } catch (error) {
                S25Util.showError("Failed to load preview. Please try again.");
                console.error("Failed to load preview tab:", error);
                return;
            }
        }
        this.activeTab.set(tab);
    };

    loadPreviewTab = async (): Promise<void> => {
        const activeFields = S25Util.deepCopy(this.fieldDefinitions.filter((fieldDef) => !fieldDef.isDeleted));
        this.stampMissingCustomAttributeTypes(activeFields);
        this.previewFieldDefinitionsClone.set(activeFields);
        const rules = await loadIngestionShowHideRules(this.ingestionPointId, activeFields, this.itemService);
        this.previewShowHideRules.set(rules);
        this.previewRemountKey.update((key) => key + 1);
        this.cd.markForCheck();
    };

    undeleteForm = async () => {
        try {
            await DataIngestionService.undeleteIngestionPoint(this.ingestionPointId);
            this.onSave.emit();
        } catch (err) {
            S25Util.showError(err);
        }
    };

    ngOnInit() {
        this.loadAvailableWorkflows();
        this.loadReportingFieldCodes();
        this.refresh();
    }

    loadReportingFieldCodes = async () => {
        try {
            this.reportingFieldCodes = await DataIngestionService.getReportingFieldCodes();
            this.cd.detectChanges();
        } catch (err) {
            console.error("Failed to load reporting field codes:", err);
        }
    };

    onReportingFieldCodeSelect = (fieldDef: IngestionFieldDefinition, selectedCode: string) => {
        if (selectedCode) {
            fieldDef.reportingFieldCode = selectedCode;
            this.onReportingCodeChange(fieldDef);
        }
    };

    async loadAvailableWorkflows() {
        try {
            this.availableWorkflows.set(await WorkflowService.getWorkflowProcesses(true));
            this.computeReferencingWorkflows();
            this.cd.detectChanges();
        } catch (error) {
            console.error("Error loading workflow processes:", error);
        }
    }

    private computeReferencingWorkflows() {
        const formId = this.ingestionPointId;
        if (!formId) return;

        this.referencingWorkflows = this.availableWorkflows().filter((wf) =>
            wf.stages?.some((stage) =>
                stage.actions?.some((action) => {
                    if (action.actionItemId === formId) return true;
                    if (action.spawnChildParentItemId === formId) return true;
                    return action.dispatchMappings?.some((dm) => dm.targetIngestionPointId === formId);
                }),
            ),
        );
    }

    onWorkflowTypeChange() {
        // Clear the other workflow field when switching types
        if (this.workflowAssignmentType === "direct") {
            this.ingestionPoint.defaultWorkflowId = undefined;
            this.selectedDefaultWorkflowId.set(undefined);
        } else if (this.workflowAssignmentType === "default") {
            this.ingestionPoint.workflowId = undefined;
            this.selectedWorkflowId.set(undefined);
        } else if (this.workflowAssignmentType === "none") {
            this.ingestionPoint.workflowId = undefined;
            this.ingestionPoint.defaultWorkflowId = undefined;
            this.selectedWorkflowId.set(undefined);
            this.selectedDefaultWorkflowId.set(undefined);
        }
        this.cd.detectChanges();
    }

    onDirectWorkflowDropdownChange(item: DropDownItem | null | undefined): void {
        const workflowId = item?.itemId != null ? Number(item.itemId) : undefined;
        this.ingestionPoint.workflowId = workflowId;
        this.selectedWorkflowId.set(workflowId);
        this.cd.detectChanges();
    }

    onDefaultWorkflowDropdownChange(item: DropDownItem | null | undefined): void {
        const workflowId = item?.itemId != null ? Number(item.itemId) : undefined;
        this.ingestionPoint.defaultWorkflowId = workflowId;
        this.selectedDefaultWorkflowId.set(workflowId);
        this.cd.detectChanges();
    }

    isAssignedWorkflowDisabled(): boolean {
        if (!this.ingestionPoint.workflowId) return false;
        return !this.availableWorkflows().some(
            (w) => String(w.workflowProcessId) === String(this.ingestionPoint.workflowId),
        );
    }

    determineWorkflowAssignmentType() {
        if (this.ingestionPoint.workflowId) {
            this.workflowAssignmentType = "direct";
        } else if (this.ingestionPoint.defaultWorkflowId) {
            this.workflowAssignmentType = "default";
        } else {
            this.workflowAssignmentType = "none";
        }
    }

    getDefaultValuePlaceholder(fieldDef: IngestionFieldDefinition): string {
        switch (fieldDef.answerType) {
            case "JSON":
                return "";
            case "INTEGER":
                return "e.g., 123";
            case "FLOAT":
                return "e.g., 123.45";
            case "BOOLEAN":
                return "true or false";
            case "DATETIME":
                return "e.g., 2024-01-15T10:30:00";
            case "STRING":
            case "LARGE_STRING":
                return "e.g., Default text value";
            case "FILE":
                return "File path or URL";
            default:
                if (fieldDef.fieldType === "MULTISELECT") {
                    return "e.g., Option 1|Option 2 (use | to separate multiple values)";
                }
                return "Enter default value";
        }
    }

    isValidDefaultValue(fieldDef: IngestionFieldDefinition): string[] | null {
        if (!fieldDef.defaultValue || fieldDef.defaultValue.trim() === "") {
            return null; // Empty is valid (optional field)
        }

        const value = fieldDef.defaultValue.trim();
        const errors: string[] = [];

        switch (fieldDef.answerType) {
            case "JSON":
                break;

            case "INTEGER":
                const intValue = parseInt(value, 10);
                if (isNaN(intValue) || intValue.toString() !== value) {
                    errors.push("Must be a valid integer (e.g., 123)");
                }
                break;

            case "FLOAT":
                const floatValue = parseFloat(value);
                if (isNaN(floatValue) || !isFinite(floatValue)) {
                    errors.push("Must be a valid number (e.g., 123.45)");
                }
                break;

            case "BOOLEAN":
                const lowerValue = value.toLowerCase();
                if (!["true", "false"].includes(lowerValue)) {
                    errors.push("Must be 'true' or 'false'");
                }
                break;

            case "DATETIME":
                const date = new Date(value);
                if (isNaN(date.getTime())) {
                    errors.push("Must be a valid date/time (e.g., 2024-01-15T10:30:00)");
                }
                break;

            case "STRING":
            case "LARGE_STRING":
            case "FILE":
                if (fieldDef.options && fieldDef.options.length > 0) {
                    if (fieldDef.fieldType === "MULTISELECT" || fieldDef.fieldType === "CHECKBOX") {
                        // For multiselect, split by | and validate each value
                        const values = value
                            .split("|")
                            .map((v) => v.trim())
                            .filter((v) => v);
                        const invalidValues = values.filter((v) => !fieldDef.options!.some((opt) => opt.option === v));
                        if (invalidValues.length > 0) {
                            errors.push(
                                `Invalid values: ${invalidValues.join(", ")}. Must match available options: ${fieldDef.options.map((opt) => opt.option).join(", ")}`,
                            );
                        }
                    } else if (fieldDef.fieldType === "SELECT" || fieldDef.fieldType === "RADIOBUTTON") {
                        const hasMatchingOption = fieldDef.options.some((opt) => opt.option === value);
                        if (!hasMatchingOption) {
                            errors.push(
                                `Must match one of the available options: ${fieldDef.options.map((opt) => opt.option).join(", ")}`,
                            );
                        }
                    }
                }
                break;
        }

        return errors.length > 0 ? errors : null;
    }

    setObjectOccurrenceFieldConfigFlag(
        fieldDef: IngestionFieldDefinition,
        key: keyof ObjectOccurrenceFieldConfig,
        value: boolean,
    ) {
        const cur = parseObjectOccurrenceFieldConfig(fieldDef.fieldConfig);
        const next: ObjectOccurrenceFieldConfig = { ...cur, [key]: value };
        fieldDef.fieldConfig = JSON.stringify({
            showLocations: next.showLocations,
            showResources: next.showResources,
        });
        this.cd.markForCheck();
    }

    getCategoriesAdminFilterSelected(fieldDef: IngestionFieldDefinition): S25ItemI[] {
        return this.categoriesFieldConfigAdminSelection.get(fieldDef) ?? [];
    }

    setCategoriesFieldConfigSingleSelect(fieldDef: IngestionFieldDefinition, value: boolean) {
        const cur = parseCategoriesFieldConfig(fieldDef.fieldConfig);
        fieldDef.fieldConfig = stringifyCategoriesFieldConfig({ ...cur, singleSelect: value });
        this.cd.markForCheck();
    }

    setCategoriesFieldConfigExpandAutomatically(fieldDef: IngestionFieldDefinition, value: boolean) {
        const cur = parseCategoriesFieldConfig(fieldDef.fieldConfig);
        fieldDef.fieldConfig = stringifyCategoriesFieldConfig({ ...cur, expandAutomatically: value });
        this.cd.markForCheck();
    }

    setCategoriesFieldConfigButtonLabel(fieldDef: IngestionFieldDefinition, value: string) {
        const cur = parseCategoriesFieldConfig(fieldDef.fieldConfig);
        fieldDef.fieldConfig = stringifyCategoriesFieldConfig({ ...cur, buttonLabel: value });
        this.cd.markForCheck();
    }

    onCategoriesFieldConfigItemsChange(fieldDef: IngestionFieldDefinition, items: S25ItemI[]) {
        const cur = parseCategoriesFieldConfig(fieldDef.fieldConfig);
        const nextItems = items || [];
        this.categoriesFieldConfigAdminSelection.set(fieldDef, [...nextItems]);
        fieldDef.fieldConfig = stringifyCategoriesFieldConfig({
            ...cur,
            categoryIds: nextItems.map((i) => Number(i.itemId)).filter((n) => Number.isFinite(n) && n > 0),
        });
        this.cd.markForCheck();
    }

    getFormStyleConfig(): FormStyleConfig {
        return parseFormStyleConfig(this.ingestionPoint?.formStyle);
    }

    formHeaderImageFileKey(): string {
        return `ingestion-form-header&${this.ingestionPointId}`;
    }

    setFormStyleHeaderImageKey(value: string) {
        const headerImageKey = value?.trim() || undefined;
        this.updateFormStyleConfig({ ...this.getFormStyleConfig(), headerImageKey });
    }

    setFormStyleTitleFontSize(value: FormFontSize) {
        this.updateFormStyleConfig({ ...this.getFormStyleConfig(), titleFontSize: value });
    }

    setFormStyleTitleColor(value: string) {
        const normalized = S25Util.hexForColorPicker(value, DEFAULT_FORM_TITLE_COLOR);
        this.updateFormStyleConfig({
            ...this.getFormStyleConfig(),
            titleColor: normalized === DEFAULT_FORM_TITLE_COLOR ? undefined : normalized,
        });
    }

    private updateFormStyleConfig(config: FormStyleConfig) {
        this.ingestionPoint.formStyle = stringifyFormStyleConfig(config);
        this.cd.markForCheck();
    }

    canUploadSectionHeaderImage(fieldDef: IngestionFieldDefinition): boolean {
        return !!fieldDef.fieldDefinitionId;
    }

    sectionHeaderImageFileKey(fieldDef: IngestionFieldDefinition): string | undefined {
        if (!this.canUploadSectionHeaderImage(fieldDef)) {
            return undefined;
        }
        return `ingestion-section-header&${this.ingestionPointId}&${fieldDef.fieldDefinitionId}`;
    }

    setSectionFieldConfigHeaderImageKey(fieldDef: IngestionFieldDefinition, value: string) {
        const cur = parseSectionFieldConfig(fieldDef.fieldConfig);
        const headerImageKey = value?.trim() || undefined;
        this.updateSectionFieldConfig(fieldDef, { ...cur, headerImageKey });
    }

    setSectionFieldConfigHeadingFontSize(fieldDef: IngestionFieldDefinition, value: FormFontSize) {
        const cur = parseSectionFieldConfig(fieldDef.fieldConfig);
        this.updateSectionFieldConfig(fieldDef, { ...cur, headingFontSize: value });
    }

    setSectionFieldConfigHeadingColor(fieldDef: IngestionFieldDefinition, value: string) {
        const cur = parseSectionFieldConfig(fieldDef.fieldConfig);
        const normalized = S25Util.hexForColorPicker(value, DEFAULT_SECTION_HEADING_COLOR);
        this.updateSectionFieldConfig(fieldDef, {
            ...cur,
            headingColor: normalized === DEFAULT_SECTION_HEADING_COLOR ? undefined : normalized,
        });
    }

    private updateSectionFieldConfig(fieldDef: IngestionFieldDefinition, config: SectionFieldConfig) {
        fieldDef.fieldConfig = stringifySectionFieldConfig(config, fieldDef.fieldConfig);
        this.cd.markForCheck();
    }

    private async hydrateCategoriesFieldConfigAdminSelections(fieldDefs: IngestionFieldDefinition[]) {
        for (const fd of fieldDefs) {
            if (fd.fieldType !== "CATEGORIES" && fd.fieldType !== "ORG_CATEGORIES") {
                continue;
            }
            const cfg = parseCategoriesFieldConfig(fd.fieldConfig);
            if (!cfg.categoryIds.length) {
                this.categoriesFieldConfigAdminSelection.set(fd, []);
                continue;
            }
            const criteriaType: SearchCriteriaType["type"] =
                fd.fieldType === "CATEGORIES" ? "eventCategories" : "organizationCategories";
            const items: S25ItemI.NumId[] = cfg.categoryIds.map((id) => ({ itemId: id }));
            await this.searchCriteriaDataUtil.enrichItemsWithItemName(criteriaType, items);
            this.categoriesFieldConfigAdminSelection.set(fd, items);
        }
    }

    /**
     * Checks if a field definition is a system-defined field (ID < 0)
     * System-defined fields cannot be edited or removed by users
     */
    isSystemField(fieldDef: IngestionFieldDefinition): boolean {
        return fieldDef.fieldDefinitionId != null && fieldDef.fieldDefinitionId < 0;
    }

    validateDefaultValues(): string[] {
        const errors: string[] = [];

        this.fieldDefinitions.forEach((fieldDef, index) => {
            const fieldErrors = this.isValidDefaultValue(fieldDef);
            if (fieldErrors) {
                fieldErrors.forEach((error) => {
                    errors.push(`Field "${fieldDef.fieldLabel}" (${index + 1}): ${error}`);
                });
            }
        });

        return errors;
    }

    isJsonField(fieldDef: IngestionFieldDefinition): boolean {
        return DataIngestionService.isJsonField(fieldDef);
    }

    isOptionBasedFieldType(fieldType: string | undefined): boolean {
        return (
            fieldType === "SELECT" ||
            fieldType === "MULTISELECT" ||
            fieldType === "CHECKBOX" ||
            fieldType === "RADIOBUTTON"
        );
    }

    validateOptionLengths(): string[] {
        const errors: string[] = [];

        this.fieldDefinitions.forEach((fieldDef, index) => {
            if (fieldDef.options && fieldDef.options.length > 0) {
                fieldDef.options.forEach((option, optIndex) => {
                    if (option.option && !this.isValidOptionLength(option.option)) {
                        const fieldLabel = fieldDef.fieldLabel || `Field ${index + 1}`;
                        errors.push(
                            `${fieldLabel}, Option ${optIndex + 1}: Option cannot exceed ${IngestionAdminFormComponent.MAX_OPTION_LENGTH} characters (current length: ${option.option.length})`,
                        );
                    }
                });
            }
        });

        return errors;
    }

    validateMinMaxRanges = (): string[] => {
        const errors: string[] = [];
        const minLimit = this.starsNumericScaleMin();
        const maxLimit = this.starsNumericScaleMax();
        this.fieldDefinitions.forEach((fieldDef, index) => {
            const fieldLabel = fieldDef.fieldLabel || `Field ${index + 1}`;
            if (fieldDef.minLength != null && fieldDef.maxLength != null && fieldDef.minLength > fieldDef.maxLength) {
                errors.push(`${fieldLabel}: Minimum value cannot be greater than maximum value`);
            }
            if (fieldDef.fieldType === "STARS_RATING" || fieldDef.fieldType === "NUMERIC_SCALE") {
                const minVal = fieldDef.minLength;
                const maxVal = fieldDef.maxLength;
                if (minVal != null && (Number.isNaN(minVal) || minVal < minLimit || minVal > maxLimit)) {
                    errors.push(`${fieldLabel}: Minimum must be between ${minLimit} and ${maxLimit}`);
                }
                if (maxVal != null && (Number.isNaN(maxVal) || maxVal < minLimit || maxVal > maxLimit)) {
                    errors.push(`${fieldLabel}: Maximum must be between ${minLimit} and ${maxLimit}`);
                }
            }
        });
        return errors;
    };

    ensureBooleanOptions(fieldDef: IngestionFieldDefinition) {
        if (!this.isOptionBasedFieldType(fieldDef.fieldType)) {
            return;
        }
        // Ensure we have exactly "true" and "false" options
        const hasTrue = fieldDef.options?.some((opt) => opt.option === "true");
        const hasFalse = fieldDef.options?.some((opt) => opt.option === "false");

        if (!fieldDef.options) {
            fieldDef.options = [];
        }

        // Remove any non-boolean options
        fieldDef.options = fieldDef.options.filter((opt) => opt.option === "true" || opt.option === "false");

        // Add missing boolean options
        if (!hasTrue) {
            fieldDef.options.push({ option: "true", fieldDefinitionId: fieldDef.fieldDefinitionId });
        }
        if (!hasFalse) {
            fieldDef.options.push({ option: "false", fieldDefinitionId: fieldDef.fieldDefinitionId });
        }

        // Ensure sortOrder is set
        const trueOpt = fieldDef.options.find((opt) => opt.option === "true");
        const falseOpt = fieldDef.options.find((opt) => opt.option === "false");
        if (trueOpt) trueOpt.sortOrder = 1;
        if (falseOpt) falseOpt.sortOrder = 2;
    }

    /**
     * Get dropdown item for default value of ORGANIZATION, CONTACT, ROLE, ORG_TYPE, ORG_RATING, EVENT_TYPE,
     * ELECTION_POSITION, etc.
     * For ROLE, ORG_TYPE, ORG_RATING, EVENT_TYPE, ELECTION_POSITION, names come from the dropdown list (search criteria).
     * For ORGANIZATION, CONTACT, and EVENT, optional `defaultValueName` is set when we preload names.
     */
    getDefaultValueDropdownItem(fieldDef: IngestionFieldDefinition): DropDownItem | null {
        if (!fieldDef.defaultValue || !fieldDef.defaultValue.trim()) {
            return null;
        }

        const value = parseInt(fieldDef.defaultValue.trim(), 10);
        if (isNaN(value)) {
            return null;
        }

        // For ROLE, ORG_TYPE, ORG_RATING, EVENT_TYPE, and most other fields, we don't need to fetch the name - dropdown handles it
        // For ORGANIZATION, CONTACT, and EVENT (and RESOURCE too if we ever add that) we'll have the name in the fieldDef if loaded
        let itemName: string | undefined;
        const fieldType = fieldDef.fieldType;
        if (
            (fieldType === "ORGANIZATION" || fieldType === "CONTACT" || fieldType === "EVENT") &&
            fieldDef.defaultValueName
        ) {
            itemName = fieldDef.defaultValueName;
        }

        let itemTypeId: number | undefined;
        if (fieldType === "ORGANIZATION") {
            itemTypeId = Item.Ids.Organization;
        } else if (fieldType === "CONTACT") {
            itemTypeId = Item.Ids.Contact;
        } else if (fieldType === "EVENT") {
            itemTypeId = Item.Ids.Event;
        }

        return {
            itemId: value,
            itemTypeId: itemTypeId,
            itemName: itemName || "",
            txt: itemName || "",
            val: value.toString(),
        } as DropDownItem;
    }

    /**
     * Handle default value change from dropdown
     */
    onDefaultValueChange(fieldDef: IngestionFieldDefinition, item: DropDownItem | null) {
        const fieldType = fieldDef.fieldType;
        if (item?.itemId) {
            fieldDef.defaultValue = item.itemId.toString();
            // Store the name for display (types where the dropdown does not load the label by id)
            if (fieldType === "ORGANIZATION" || fieldType === "CONTACT") {
                fieldDef.defaultValueName = item.itemName || item.txt || "";
            }
        } else {
            fieldDef.defaultValue = "";
            if (fieldDef.defaultValueName) {
                delete fieldDef.defaultValueName;
            }
        }
        this.cd.detectChanges();
    }

    /**
     * Load names for ORGANIZATION, CONTACT, and EVENT default values when editing a form
     */
    async loadDefaultValueNames(): Promise<void> {
        const orgIds: number[] = [];
        const contactIds: number[] = [];
        const eventIds: number[] = [];
        const orgFieldDefs: IngestionFieldDefinition[] = [];
        const contactFieldDefs: IngestionFieldDefinition[] = [];
        const eventFieldDefs: IngestionFieldDefinition[] = [];

        // Collect IDs and field definitions
        this.fieldDefinitions.forEach((fieldDef) => {
            if (fieldDef.defaultValue && fieldDef.defaultValue.trim()) {
                const value = parseInt(fieldDef.defaultValue.trim(), 10);
                if (!isNaN(value)) {
                    const fieldType = fieldDef.fieldType;
                    if (fieldType === "ORGANIZATION") {
                        orgIds.push(value);
                        orgFieldDefs.push(fieldDef);
                    } else if (fieldType === "CONTACT") {
                        contactIds.push(value);
                        contactFieldDefs.push(fieldDef);
                    } else if (fieldType === "EVENT") {
                        eventIds.push(value);
                        eventFieldDefs.push(fieldDef);
                    }
                }
            }
        });

        // Load organization names
        if (orgIds.length > 0) {
            try {
                const orgNames = await NameService.getNames(
                    orgIds.map((id) => ({ itemId: id, itemTypeId: Item.Ids.Organization })),
                );
                orgFieldDefs.forEach((fieldDef) => {
                    const value = parseInt(fieldDef.defaultValue!.trim(), 10);
                    const key = `${value}-${Item.Ids.Organization}`;
                    const name = orgNames[key];
                    if (name) {
                        fieldDef.defaultValueName = name;
                    }
                });
            } catch (error) {
                console.error("Error loading organization names for default values:", error);
            }
        }

        // Load contact names
        if (contactIds.length > 0) {
            try {
                const contactNames = await NameService.getNames(
                    contactIds.map((id) => ({ itemId: id, itemTypeId: Item.Ids.Contact })),
                );
                contactFieldDefs.forEach((fieldDef) => {
                    const value = parseInt(fieldDef.defaultValue!.trim(), 10);
                    const key = `${value}-${Item.Ids.Contact}`;
                    const name = contactNames[key];
                    if (name) {
                        fieldDef.defaultValueName = name;
                    }
                });
            } catch (error) {
                console.error("Error loading contact names for default values:", error);
            }
        }

        // Load event names
        if (eventIds.length > 0) {
            try {
                const eventNames = await NameService.getNames(
                    eventIds.map((id) => ({ itemId: id, itemTypeId: Item.Ids.Event })),
                );
                eventFieldDefs.forEach((fieldDef) => {
                    const value = parseInt(fieldDef.defaultValue!.trim(), 10);
                    const key = `${value}-${Item.Ids.Event}`;
                    const name = eventNames[key];
                    if (name) {
                        fieldDef.defaultValueName = name;
                    }
                });
            } catch (error) {
                console.error("Error loading event names for default values:", error);
            }
        }
    }
}
